import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import Card from '../components/UI/Card';
import golkaLogo from '../assets/golka-logo.png';

const STORAGE_COOLDOWN_UNTIL = 'forgot_cooldown_until';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [sent, setSent] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(() => {
    const until = localStorage.getItem(STORAGE_COOLDOWN_UNTIL);
    if (until) {
      return Math.max(0, Math.floor((parseInt(until, 10) - Date.now()) / 1000));
    }
    return 0;
  });

  const { forgotPassword } = useAuth();

  useEffect(() => {
    if (cooldownRemaining > 0) {
      const expiry = Date.now() + cooldownRemaining * 1000;
      localStorage.setItem(STORAGE_COOLDOWN_UNTIL, expiry.toString());
    } else {
      localStorage.removeItem(STORAGE_COOLDOWN_UNTIL);
    }
  }, [cooldownRemaining]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setTimeout(() => setCooldownRemaining(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownRemaining]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email обовʼязковий';
    else if (!email.includes('@')) newErrors.email = 'Невірний формат email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    setGeneralError('');
    if (!validateForm()) return;
    if (cooldownRemaining > 0) return;

    setLoading(true);
    const result = await forgotPassword(email);
    setLoading(false);

    if (result.success) {
      setSent(true);
      setCooldownRemaining(60);
    } else {
      setGeneralError(result.message || 'Не вдалося надіслати лист. Спробуйте пізніше.');
    }
  };

  const isButtonDisabled = loading || cooldownRemaining > 0;
  const buttonText = loading
    ? 'Надсилаємо...'
    : cooldownRemaining > 0
      ? `Зачекайте ${cooldownRemaining}с`
      : 'Надіслати посилання';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link to="/login" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            До входу
          </Link>
        </div>
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src={golkaLogo} alt="Голка" className="h-10 w-auto mx-auto" />
          </Link>
        </div>
        <Card>
          <div className="p-8">
            {sent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Перевірте пошту</h2>
                <p className="text-gray-600 mb-4">
                  Якщо обліковий запис з адресою <strong>{email}</strong> існує, ми надіслали на неї
                  посилання для встановлення нового пароля. Посилання дійсне 12 годин.
                </p>
                <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-800">
                  Не бачите листа? Перевірте теку «Спам».
                </div>
                <Link to="/login">
                  <Button variant="primary">Повернутися до входу</Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Відновлення пароля</h2>
                  <p className="text-gray-600 mt-2">
                    Введіть email — надішлемо посилання для встановлення нового пароля
                  </p>
                </div>
                {generalError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    {generalError}
                  </div>
                )}
                <div>
                  <Input
                    label="Email"
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={errors.email}
                    required
                    placeholder="your@email.com"
                    disabled={cooldownRemaining > 0}
                  />
                  {cooldownRemaining > 0 && (
                    <div className="bg-yellow-50 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                      ⏱ Зачекайте {cooldownRemaining} секунд перед наступною спробою.
                    </div>
                  )}
                  <Button
                    onClick={handleSubmit}
                    variant="primary"
                    size="lg"
                    className="w-full mt-2"
                    disabled={isButtonDisabled}
                  >
                    {buttonText}
                  </Button>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Згадали пароль?{' '}
                    <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                      Увійти
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
